import React from 'react';
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme from './theme/index';
import './styles/globals.css';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotFound from './pages/NotFound';

// Pages imports
import Home from './pages/Home/Home';
import Academy from './pages/Academy/Academy.jsx';
import Courses from './pages/Academy/Courses.jsx';
import Certifications from './pages/Academy/Certifications.jsx';
import CertificateVerify from './pages/Academy/CertificateVerify.jsx';
import AITutoring from './pages/Academy/AITutoring.jsx';
import CareerPaths from './pages/Academy/CareerPaths.jsx';
import StudentPortal from './pages/Academy/StudentPortal.jsx';
import CourseViewer from './pages/Academy/CourseViewer/CourseViewer.jsx';
import CoachingMentoring from './pages/Academy/CoachingMentoring.jsx';
import MentorStudio from './pages/Academy/MentorStudio.jsx';
import StrategyPlanning from './pages/Academy/StrategyPlanning.jsx';

// Virtual Labs Components
import VirtualLabs from './pages/VirtualLabs/VirtualLabs.jsx';
import SemiconOverview from './pages/VirtualLabs/semicon-mech/SemiconOverview.jsx';
import WarpageBench from './pages/VirtualLabs/semicon-mech/WarpageBench.jsx';
import PeelShearBench from './pages/VirtualLabs/semicon-mech/PeelShearBench.jsx';
import DefenceBench from './pages/VirtualLabs/semicon-mech/DefenceBench.jsx';
import PhysicsOverview from './pages/VirtualLabs/physics/PhysicsOverview.jsx';
import ForcesBench from './pages/VirtualLabs/physics/ForcesBench.jsx';
import EnergyBench from './pages/VirtualLabs/physics/EnergyBench.jsx';
import RotationBench from './pages/VirtualLabs/physics/RotationBench.jsx';
import VibrationBench from './pages/VirtualLabs/physics/VibrationBench.jsx';
import FluidsBench from './pages/VirtualLabs/physics/FluidsBench.jsx';
import HeatBench from './pages/VirtualLabs/physics/HeatBench.jsx';



// QoreConnect (community)
import QoreConnectLayout from './pages/Community/QoreConnectLayout.jsx';
import QoreConnectHub from './pages/Community/QoreConnectHub.jsx';
import Forum from './pages/Community/Forum.jsx';
import Mentorship from './pages/Community/Mentorship.jsx';
import JobBoard from './pages/Community/jobBoard.jsx';

// Other pages
import Contact from './components/forms/ContactForm.jsx';
import About from './pages/About/About.jsx';
import HardcoreEngineering from './pages/Qoreconsulting/HardCoreEngineering.jsx';

// Admin and Content Creator Dashboards
import AdminDashboard from './pages/Admin/AdminDashboard.jsx';
import ContentCreatorDashboard from './pages/Admin/ContentCreatorDashboard.jsx';
import StudentDashboard from './pages/Admin/StudentDashboard.jsx';
import QoreTalksDashboard from './pages/QoreTalks/QoreTalksDashboard.jsx';
import QoreTalksManage from './pages/QoreTalks/QoreTalksManage.jsx';
import { Toaster } from 'react-hot-toast';
import TalkDetail from './pages/QoreTalks/TalkDetail.jsx';
import LiveShow from './pages/QoreTalks/LiveShow.jsx';
import GuestApply from './pages/QoreTalks/GuestApply.jsx';
import QoreTalksSubscribe from './pages/QoreTalks/Subscribe.jsx';

// Authentication
import Login from './pages/Auth/Login.jsx';
import Register from './pages/Auth/Register.jsx';

// AI Tutor
import AITutor from './components/ai/AITutor';

// Learning Path
import SkillBrowser from './pages/LearningPath/SkillBrowser';
import CreatePathWizard from './pages/LearningPath/CreatePathWizardNew';
import PathDashboard from './pages/LearningPath/PathDashboard';

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="bottom-center" toastOptions={{ style: { background: '#1e293b', color: '#f8fafc' } }} />
          <ErrorBoundary>
        <Routes>
          {/* Home Route */}
          <Route path="/" element={<Home />} />

          {/* Academy Routes */}
          <Route path="/academy" element={<Academy />} />
          <Route path="/courses" element={<Courses />} />
          <Route path="/skills" element={<SkillBrowser />} />
          <Route path="/create-path" element={
            <ProtectedRoute allowedRoles={['student']}>
              <CreatePathWizard />
            </ProtectedRoute>
          } />
          <Route path="/path-dashboard" element={
            <ProtectedRoute allowedRoles={['student']}>
              <PathDashboard />
            </ProtectedRoute>
          } />
          <Route path="/certifications" element={<Certifications />} />
          <Route path="/verify/:certificateId" element={<CertificateVerify />} />
          <Route path="/aitutoring" element={<AITutoring />} />
          <Route path="/careerpaths" element={<CareerPaths />} />
          <Route path="/studentportal" element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentPortal />
            </ProtectedRoute>
          } />
          <Route path="/studentportal/mycourses/:courseId" element={
            <ProtectedRoute allowedRoles={['student']}>
              <CourseViewer />
            </ProtectedRoute>
          } />
          <Route path="/coachingmentoring" element={<CoachingMentoring />} />
          <Route path="/mentor-dashboard" element={
            <ProtectedRoute>
              <MentorStudio />
            </ProtectedRoute>
          } />
          <Route path="/strategyplanning" element={<StrategyPlanning />} />

          {/* Virtual Labs Routes - Most specific routes first */}
          <Route path="/virtuallabs/semicon-mech/warpage" element={<WarpageBench />} />
          <Route path="/virtuallabs/semicon-mech/peel-shear" element={<PeelShearBench />} />
          <Route path="/virtuallabs/semicon-mech/defence" element={<DefenceBench />} />
          <Route path="/virtuallabs/semicon-mech" element={<SemiconOverview />} />

          <Route path="/virtuallabs/physics/forces" element={<ForcesBench />} />
          <Route path="/virtuallabs/physics/energy" element={<EnergyBench />} />
          <Route path="/virtuallabs/physics/rotation" element={<RotationBench />} />
          <Route path="/virtuallabs/physics/vibration" element={<VibrationBench />} />
          <Route path="/virtuallabs/physics/fluids" element={<FluidsBench />} />
          <Route path="/virtuallabs/physics/heat" element={<HeatBench />} />
          <Route path="/virtuallabs/physics" element={<PhysicsOverview />} />
          <Route path="/virtuallabs/medphysics/*" element={<Navigate to="/virtuallabs/physics" replace />} />
          <Route path="/virtuallabs/medphysics" element={<Navigate to="/virtuallabs/physics" replace />} />
          <Route path="/virtuallabs" element={<VirtualLabs />} />

          {/* Podcast legacy routes → QoreTalks */}
          <Route path="/podcast" element={<Navigate to="/qoretalks" replace />} />
          <Route path="/episodes" element={<Navigate to="/qoretalks" replace />} />
          <Route path="/transcripts" element={<Navigate to="/qoretalks" replace />} />
          <Route path="/PodcastDashboard" element={<Navigate to="/qoretalks-dashboard" replace />} />
          <Route path="/CommentSystem" element={<Navigate to="/qoretalks" replace />} />
          <Route path="/live" element={<Navigate to="/qoretalks/live" replace />} />
          <Route path="/guests" element={<Navigate to="/qoretalks/apply" replace />} />
          <Route path="/subscribe" element={<Navigate to="/qoretalks/subscribe" replace />} />

          {/* Other Routes */}
          <Route path="/contact" element={<Contact />} />
          <Route path="/About" element={<About />} />

          {/* QoreConnect Routes */}
          <Route path="/qoreconnect" element={<QoreConnectLayout />}>
            <Route index element={<QoreConnectHub />} />
            <Route path="forum" element={<Forum />} />
            <Route path="mentorship" element={<Mentorship />} />
            <Route path="jobs" element={<JobBoard />} />
            <Route path="networking" element={<Navigate to="/qoreconnect" replace />} />
            <Route path="events" element={<Navigate to="/qoreconnect" replace />} />
            <Route path="partnerships" element={<Navigate to="/qoreconnect" replace />} />
            <Route path="*" element={<Navigate to="/qoreconnect" replace />} />
          </Route>
          <Route path="/community" element={<Navigate to="/qoreconnect" replace />} />
          <Route path="/community/Forum" element={<Navigate to="/qoreconnect/forum" replace />} />
          <Route path="/community/forum" element={<Navigate to="/qoreconnect/forum" replace />} />
          <Route path="/community/networking" element={<Navigate to="/qoreconnect" replace />} />
          <Route path="/community/mentorship" element={<Navigate to="/qoreconnect/mentorship" replace />} />
          <Route path="/community/jobboard" element={<Navigate to="/qoreconnect/jobs" replace />} />
          <Route path="/community/jobs" element={<Navigate to="/qoreconnect/jobs" replace />} />
          <Route path="/community/events" element={<Navigate to="/qoreconnect" replace />} />
          <Route path="/community/partnerships" element={<Navigate to="/qoreconnect" replace />} />

          {/* Consulting Routes */}
          <Route path="/Qoreconsult" element={<HardcoreEngineering />} />

          {/* Authentication Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Admin and Content Creator Routes */}
          <Route path="/admin-dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin-dashboard-test" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/creator-dashboard" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <ContentCreatorDashboard />
            </ProtectedRoute>
          } />
          <Route path="/student-dashboard" element={
            <ProtectedRoute allowedRoles={['student']}>
              <StudentDashboard />
            </ProtectedRoute>
          } />

          {/* QoreTalks Routes */}
          <Route path="/qoretalks" element={<QoreTalksDashboard />} />
          <Route path="/qoretalks/episodes/:id" element={<TalkDetail />} />
          <Route path="/qoretalks/live/:roomId" element={<LiveShow />} />
          <Route path="/qoretalks/live" element={<LiveShow />} />
          <Route path="/qoretalks/apply" element={<GuestApply />} />
          <Route path="/qoretalks/subscribe" element={<QoreTalksSubscribe />} />
          <Route path="/qoretalks/manage" element={
            <ProtectedRoute allowedRoles={['admin', 'instructor']}>
              <QoreTalksManage />
            </ProtectedRoute>
          } />
          <Route path="/qoretalks-dashboard" element={
            <ProtectedRoute allowedRoles={['admin', 'instructor']}>
              <Navigate to="/qoretalks/manage" replace />
            </ProtectedRoute>
          } />
          {/* 404 Catch-all */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        
        {/* AI Tutor - Available on all pages after login */}
          <AITutor />

          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;